import { useState, useEffect, useRef } from "react";
import { AudioLines, Loader2, AlertTriangle, Languages, Tags, RefreshCw, ChevronDown, Search, Check, Clock, Sparkles, ArrowLeft, RotateCw } from "lucide-react";

const LANG_LABEL = { te: "Telugu", hi: "Hindi", en: "English" };
const fmt = (s) => { s = Math.max(0, Math.round(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

/**
 * Transcript-first editor. Normal editable paragraph (not word tokens) with metadata,
 * search, a Synchronize / Synchronize-Again button-state machine, and an AI Summary
 * (verification-only) view that never overwrites the editable transcript.
 */
export default function ParagraphTranscript({
  initialText, language, audioDuration, onSynchronize, syncing, syncStage, syncedText,
  languageChoice, speechIncomplete, onChooseLanguage, initialVocabulary, onApplyKeywords, retranscribing,
  onSummarize, initialSummary, initialSummarySource, transcriptMismatch, coverage, onTextChange, languageProbability,
}) {
  const [text, setText] = useState(initialText || "");
  const [vocab, setVocab] = useState(initialVocabulary || "");
  const [kwOpen, setKwOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const findFrom = useRef(0);

  // AI Summary state — kept entirely separate from the editable transcript.
  const [view, setView] = useState("transcript"); // transcript | summary
  const [summary, setSummary] = useState(initialSummary || null);
  const [summarySrc, setSummarySrc] = useState(initialSummarySource ?? null); // exact text the summary was built from
  const [summarizing, setSummarizing] = useState(false);
  const [summaryErr, setSummaryErr] = useState(null);

  useEffect(() => { setText(initialText || ""); }, [initialText]);
  useEffect(() => { setVocab(initialVocabulary || ""); }, [initialVocabulary]);
  useEffect(() => { setSummary(initialSummary || null); }, [initialSummary]);
  useEffect(() => { setSummarySrc(initialSummarySource ?? null); }, [initialSummarySource]);

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const sentences = text.trim() ? (text.split(/[.!?…]+/).filter((s) => s.trim()).length || 1) : 0;
  const dirty = (text || "").trim() !== ((syncedText || "").trim());
  const isSynced = syncedText != null && !dirty;
  const hasText = !!text.trim();
  // Summary is stale when the current transcript differs from the text it was generated from.
  const summaryStale = summary != null && summarySrc != null && (text || "").trim() !== (summarySrc || "").trim();

  const runSearch = (val) => {
    const el = ref.current; const term = (val ?? q); if (!el || !term) return;
    const hay = text.toLowerCase(); const needle = term.toLowerCase();
    let idx = hay.indexOf(needle, findFrom.current);
    if (idx === -1) idx = hay.indexOf(needle, 0); // wrap
    if (idx === -1) return;
    el.focus(); el.setSelectionRange(idx, idx + term.length);
    findFrom.current = idx + term.length;
  };

  const doSummarize = async () => {
    if (summarizing || !hasText) return;
    setSummarizing(true); setSummaryErr(null);
    const src = text; // snapshot the CURRENT edited transcript (source of truth)
    try {
      const res = await onSummarize(src);
      setSummary(res.summary);
      setSummarySrc(src);
      setView("summary");
    } catch (e) {
      const to = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      setSummaryErr(to
        ? "Summary generation is taking too long. Please try again."
        : "AI summary couldn't be generated. Your original transcription is safe.");
    }
    setSummarizing(false);
  };

  // Clicking "AI Summary": reuse an existing summary instantly (no LLM call); only generate
  // when there is no summary yet. Stale summaries are refreshed via "Update Summary".
  const openSummary = () => {
    if (summarizing || !hasText) return;
    if (summary != null) { setSummaryErr(null); setView("summary"); return; }
    doSummarize();
  };

  return (
    <div className="flex h-full flex-col bg-[#0d0d0d]" data-testid="paragraph-transcript">
      <div className="border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="font-display text-lg font-black tracking-tight text-white">Transcript</div>
          {view === "summary" && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#B26BFF]/40 bg-[#B26BFF]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#c99bff]" data-testid="summary-mode-badge">
              <Sparkles size={10} /> AI Summary
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[#A1A1AA]" data-testid="transcript-meta">
          <span>{words} words</span><span>·</span><span>{sentences} sentence{sentences === 1 ? "" : "s"}</span>
          {audioDuration ? <><span>·</span><span className="inline-flex items-center gap-1"><Clock size={11} /> {fmt(audioDuration)}</span></> : null}
          {language ? <><span>·</span><span className="capitalize" data-testid="detected-language">{language}{typeof languageProbability === "number" ? ` (${Math.round(languageProbability * 100)}% conf.)` : ""}</span></> : null}
        </div>
      </div>

      {view === "transcript" && (
        <div className="border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5">
            <Search size={14} className="text-[#666]" />
            <input data-testid="transcript-search" value={q}
              onChange={(e) => { setQ(e.target.value); findFrom.current = 0; }}
              onKeyDown={(e) => { if (e.key === "Enter") runSearch(e.currentTarget.value); }}
              placeholder="Search words…" className="w-full bg-transparent py-1.5 text-sm text-white outline-none placeholder:text-[#666]" />
          </div>
        </div>
      )}

      {view === "transcript" && languageChoice && (
        <div className="border-b border-[#FFD400]/30 bg-[#FFD400]/[0.07] px-4 py-3" data-testid="language-choice-banner">
          <div className="flex items-start gap-2 text-[12px] text-[#f0d071]">
            <Languages size={14} className="mt-0.5 shrink-0" />
            <span>Language detected with low confidence (looks like <b>{languageChoice.detected_label || LANG_LABEL[languageChoice.detected]}</b>). Confirm the spoken language:</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(languageChoice.options || ["te", "hi", "en"]).map((code) => (
              <button key={code} data-testid={`lang-choice-${code}`} disabled={retranscribing} onClick={() => onChooseLanguage(code)}
                className="rounded-lg border border-[#FFD400]/40 bg-[#FFD400]/10 px-3 py-1.5 text-xs font-semibold text-[#FFD400] hover:bg-[#FFD400]/20 disabled:opacity-50">
                Use {LANG_LABEL[code] || code}
              </button>
            ))}
          </div>
        </div>
      )}

      {view === "transcript" && transcriptMismatch && (
        <div className="flex items-start gap-2 border-b border-[#FF453A]/30 bg-[#FF453A]/[0.08] px-4 py-2.5 text-[11px] text-[#ff9a92]" data-testid="transcript-mismatch-warning">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{transcriptMismatch}</span>
        </div>
      )}

      {view === "transcript" && speechIncomplete && (
        <div className="flex items-start gap-2 border-b border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.06] px-4 py-2.5 text-[11px] text-[#f0b45a]" data-testid="incomplete-speech-warning">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>Some speech may not have been transcribed{typeof coverage === "number" ? ` (about ${coverage}% of the audio was covered)` : ""}. Add any missing text below before you synchronize.</span>
        </div>
      )}

      {/* Body: editable transcript OR the AI summary — same container, never overwrites text state. */}
      <div className="min-h-0 flex-1 overflow-hidden p-3">
        {view === "transcript" ? (
          <textarea ref={ref} data-testid="paragraph-textarea" value={text} onChange={(e) => { setText(e.target.value); onTextChange && onTextChange(e.target.value); }} spellCheck
            placeholder="Your transcript will appear here — edit it freely like a normal document…"
            className="h-full w-full resize-none rounded-xl border border-white/10 bg-white/[0.02] p-4 font-plex text-[17px] leading-[1.9] text-white/90 outline-none transition-colors focus:border-[#00F0FF]/50" />
        ) : (
          <div data-testid="summary-view" className="h-full overflow-y-auto rounded-xl border border-[#B26BFF]/25 bg-[#B26BFF]/[0.05] p-4">
            {summaryStale && (
              <div className="mb-3 flex items-center gap-1.5 rounded-lg border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.08] px-3 py-2 text-[11px] font-semibold text-[#f0b45a]" data-testid="summary-stale-indicator">
                <AlertTriangle size={13} /> Transcript changed · Summary needs updating
              </div>
            )}
            <p data-testid="summary-text" className="whitespace-pre-wrap font-plex text-[16px] leading-[1.85] text-white/90">{summary}</p>
            {summaryStale && (
              <button data-testid="update-summary-btn" disabled={summarizing} onClick={doSummarize}
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#B26BFF]/40 bg-[#B26BFF]/10 px-3 py-1.5 text-xs font-semibold text-[#c99bff] hover:bg-[#B26BFF]/20 disabled:opacity-50">
                {summarizing ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />} Update Summary
              </button>
            )}
          </div>
        )}
      </div>

      {view === "transcript" && (
        <div className="shrink-0 border-t border-white/10">
          <button data-testid="keywords-toggle" onClick={() => setKwOpen((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-2.5 text-[12px] font-semibold text-[#A1A1AA] hover:text-white">
            <span className="flex items-center gap-2"><Tags size={14} /> Vocabulary / Keywords <span className="font-normal text-[#666]">(names, brands)</span></span>
            <ChevronDown size={15} className={`transition-transform ${kwOpen ? "rotate-180" : ""}`} />
          </button>
          {kwOpen && (
            <div className="px-4 pb-3">
              <input data-testid="keywords-input" value={vocab} onChange={(e) => setVocab(e.target.value)} placeholder="Virat Kohli, Rohit Sharma, StumpTales"
                className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white/90 outline-none focus:border-[#00F0FF]/50" />
              <button data-testid="apply-keywords-btn" disabled={retranscribing} onClick={() => onApplyKeywords(vocab)}
                className="mt-2 flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 disabled:opacity-50">
                {retranscribing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Apply &amp; re-transcribe
              </button>
            </div>
          )}
        </div>
      )}

      <div className="shrink-0 border-t border-white/10 p-3">
        {summaryErr && view === "transcript" && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-[#FF453A]/30 bg-[#FF453A]/[0.08] px-3 py-2 text-[11px] text-[#ff9a92]" data-testid="summary-error">
            <span>{summaryErr}</span>
            <button data-testid="summary-retry-btn" onClick={doSummarize} className="shrink-0 rounded-md border border-[#FF453A]/40 px-2 py-1 font-semibold text-[#ff9a92] hover:bg-[#FF453A]/10">Try Again</button>
          </div>
        )}

        {syncing ? (
          <div data-testid="sync-progress" className="rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/[0.06] p-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#00F0FF]"><Loader2 size={16} className="animate-spin" /> {syncStage || "Synchronizing…"}</div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full w-1/3 animate-pulse rounded-full bg-[#00F0FF]" /></div>
          </div>
        ) : (
          <>
            {view === "transcript" ? (
              <div className={`mb-2 flex items-center gap-1.5 text-xs font-semibold ${isSynced ? "text-[#00F0FF]" : syncedText ? "text-[#FF9F0A]" : "text-[#A1A1AA]"}`} data-testid="paragraph-sync-status">
                {isSynced ? <><Check size={14} /> Audio synchronized</> : syncedText ? <><AlertTriangle size={13} /> Changes not synchronized</> : "Not yet synchronized — align to generate word timings"}
              </div>
            ) : (
              <div className="mb-2 text-xs font-semibold text-[#c99bff]" data-testid="summary-hint">AI Summary is for verification only — it never changes your transcript or captions.</div>
            )}

            {view === "transcript" && summary != null && summaryStale && (
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-[#f0b45a]" data-testid="summary-outdated-hint">
                <AlertTriangle size={12} /> Transcript changed · Summary needs updating
              </div>
            )}

            {!hasText && view === "transcript" && (
              <div className="mb-2 text-[11px] text-[#666]" data-testid="summary-empty-hint">Add or generate a transcript before creating a summary.</div>
            )}

            <div className="flex items-stretch gap-2">
              {view === "transcript" ? (
                <button data-testid="ai-summary-btn" disabled={!hasText || summarizing} onClick={openSummary}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#B26BFF]/40 bg-[#B26BFF]/10 py-3.5 text-sm font-bold text-[#c99bff] transition-colors hover:bg-[#B26BFF]/20 disabled:opacity-50">
                  {summarizing ? <><Loader2 size={17} className="animate-spin" /> Generating Summary…</> : <><Sparkles size={17} /> AI Summary{summary != null ? (summaryStale ? " · outdated" : "") : ""}</>}
                </button>
              ) : (
                <button data-testid="original-transcription-btn" onClick={() => setView("transcript")}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">
                  <ArrowLeft size={17} /> Original Transcription
                </button>
              )}
              <button data-testid="synchronize-audio-btn" disabled={!hasText} onClick={() => onSynchronize(text)}
                className="pulse-glow flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#00F0FF] to-[#0891b2] py-3.5 text-sm font-bold text-black transition-transform hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100">
                <AudioLines size={17} /> {syncedText ? "Synchronize Again" : "Synchronize with Audio"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
